import React, { useState, useEffect, useCallback } from "react";
import config from "./config";
import LeagueInviteManager from "./components/LeagueInviteManager";
import TeamSlotGrid from "./components/TeamSlotGrid";
import leagueMembershipApi from "./services/leagueMembershipApi";
import { devLog } from "./utils/devLog";
import { validateAllowedText } from "./utils/contentModeration";
import { getLeagueRosterCounts } from "./utils/draftRosterNeeds";
import { formatDraftTimeEastern } from "./utils/draftTimeUtils";
import {
  formatUnlockHour,
  getLeagueSettingsWaiverCopy,
  getPlatformDefaultWaiverSummary,
  getPostDropWaiverSettingsCopy,
  normalizeUnlockDay,
} from "./utils/transactionWindowCopy";

// Example usage:
// <LeagueSettings
//   isCommissioner={true}
//   isDraftStarted={false}
//   isPublicLeague={false}
//   leagueSettings={{ name: "My League", color: "#2563eb", scoringType: "STANDARD" }}
//   onSave={fn}
// />

const SCORING_OPTIONS = [
  { value: "STANDARD", label: "Standard" },
  { value: "HALF_PPR", label: "Half PPR" },
  { value: "PPR", label: "PPR" },
];

const DRAFT_TYPE_OPTIONS = [
  { value: "SNAKE", label: "Snake Draft" },
  { value: "SALARY_CAP", label: "Salary Cap Draft" },
];

const NOMINATION_TIMER_OPTIONS = [
  { value: 30, label: "30 seconds" },
  { value: 45, label: "45 seconds (default)" },
  { value: 60, label: "60 seconds" },
];

const SALARY_CAP_BUDGET_OPTIONS = [
  { value: 100, label: "$100" },
  { value: 200, label: "$200 (default)" },
  { value: 300, label: "$300" },
  { value: 400, label: "$400" },
  { value: 500, label: "$500" },
  { value: 600, label: "$600" },
  { value: 700, label: "$700" },
  { value: 800, label: "$800" },
  { value: 900, label: "$900" },
  { value: 1000, label: "$1000" },
];
const SALARY_CAP_BUDGET_VALUES = SALARY_CAP_BUDGET_OPTIONS.map(o => o.value);
function normalizeSalaryCap(value) {
  if (value == null) return 200;
  return SALARY_CAP_BUDGET_VALUES.includes(value) ? value : 200;
}

const PLAYOFF_TEAM_OPTIONS = [2, 4, 6, 8];

function getDefaultPlayoffTeams(teamCount) {
  if (teamCount <= 3) return 2;
  if (teamCount <= 8) return 4;
  if (teamCount <= 12) return 6;
  return 8;
}

const UNLOCK_DAY_TO_WAIVER_RUN = { tuesday: 2, wednesday: 3 };

export default function LeagueSettings({
  isCommissioner = false,
  isDraftStarted = false,
  isPublicLeague = false,
  leagueSettings = { name: "", scoringType: "STANDARD", draftType: "SNAKE", salaryCap: 200, teamCount: 10, qbCount: 1, wrCount: 2, rbCount: 2, teCount: 1, flexCount: 1, flexSilverCount: 0, flexGoldCount: 0, benchCount: 5, faabBudget: 100, playoffTeams: undefined, nominationTimerDuration: 45 },
  onSave = () => {},
  onBack = null,
  onTestLock = null,
  userRole = null,
  currentUser = null,
  draftRefresh,
  isAdminAuthenticated = false,
  leagueId = null, // Add leagueId prop
  refreshLeagueSettings = null, // Add refresh function prop
  showToast = null,
}) {
  const [settings, setSettings] = useState({
    ...leagueSettings,
    playoffTeams: leagueSettings?.playoffTeams || getDefaultPlayoffTeams(leagueSettings?.teamCount || 10),
    id: leagueId // Add id from leagueId prop
  });
  const [loading, setLoading] = useState(false);

  const [saveMsg, setSaveMsg] = useState("");
  const [showInviteManager, setShowInviteManager] = useState(false);
  const canEdit = !isPublicLeague && userRole === "commissioner" && !isDraftStarted;
  const [leagueMembers, setLeagueMembers] = useState([]);
  const [teamSlots, setTeamSlots] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [removingUserId, setRemovingUserId] = useState(null);
  const [transferEmail, setTransferEmail] = useState("");
  const [transferTeamName, setTransferTeamName] = useState("");
  const [transferring, setTransferring] = useState(false);

  const fetchLeagueSettingsFromApi = useCallback(async () => {
    if (!leagueId) return;

    setLoading(true);
    try {
      const response = await fetch(`${config.API_URL}/leagues/${leagueId}/draft-info?user_id=${currentUser?.id}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        devLog("API response data:", data);
        devLog("League name from API:", data.name);

        setSettings({
          name: data.name || "",
          scoringType: data.scoring_type || "STANDARD",
          draftType: data.draft_type || "SNAKE",
          salaryCap: normalizeSalaryCap(data.salary_cap),
          teamCount: data.team_count || 10,
          qbCount: data.qb_count || 1,
          wrCount: data.wr_count || 2,
          rbCount: data.rb_count || 2,
          teCount: data.te_count || 1,
          kCount: data.k_count ?? 1,
          flexCount: data.flex_count ?? 1,
          flexSilverCount: data.flex_silver_count ?? 0,
          flexGoldCount: data.flex_gold_count ?? 0,
          benchCount: data.bench_count ?? 5,
          faabBudget: data.faab_budget || 100,
          useFAAB: data.use_faab || false,
          rosterUnlockDay: normalizeUnlockDay(data.roster_unlock_day),
          rosterUnlockHour: data.roster_unlock_hour != null ? data.roster_unlock_hour : 5,
          tradeDeadlineWeek: data.trade_deadline_week != null ? data.trade_deadline_week : 11,
          waiverRunDay: data.waiver_run_day != null ? data.waiver_run_day : UNLOCK_DAY_TO_WAIVER_RUN[normalizeUnlockDay(data.roster_unlock_day)],
          waiverRunHour: data.waiver_run_hour != null ? data.waiver_run_hour : (data.roster_unlock_hour != null ? data.roster_unlock_hour : 5),
          waiverRunMinute: data.waiver_run_minute != null ? data.waiver_run_minute : 0,
          postDropWaiverDays: data.post_drop_waiver_days != null ? data.post_drop_waiver_days : 1,
          playoffTeams: getDefaultPlayoffTeams(data.team_count || 10),
          draft_scheduled: data.draft_scheduled || false,
          draft_date: data.draft_date || null,
          draft_time: data.draft_time || null,
          draft_timezone: data.timezone || "UTC",
          is_public: data.is_public || false,
          nominationTimerDuration: data.nomination_timer_duration != null ? data.nomination_timer_duration : 45,
          id: leagueId,
        });
      } else {
        console.error('Failed to fetch league settings');
      }
    } catch (error) {
      console.error('Error fetching league settings:', error);
    } finally {
      setLoading(false);
    }
  }, [leagueId, currentUser]);

  useEffect(() => {
    fetchLeagueSettingsFromApi();
  }, [fetchLeagueSettingsFromApi]);

  const loadMemberManagement = useCallback(async () => {
    if (!leagueId || !currentUser?.id || userRole !== "commissioner" || isPublicLeague) {
      return;
    }
    setMembersLoading(true);
    try {
      const [membersData, slotsData] = await Promise.all([
        leagueMembershipApi.listMembers(leagueId, currentUser),
        leagueMembershipApi.getTeamSlots(leagueId),
      ]);
      setLeagueMembers(membersData.members || []);
      setTeamSlots(slotsData.slots || []);
    } catch (error) {
      console.error("Error loading league members:", error);
      if (showToast) {
        showToast(error.message || "Failed to load league members", "error");
      }
    } finally {
      setMembersLoading(false);
    }
  }, [leagueId, currentUser, userRole, isPublicLeague, showToast]);

  useEffect(() => {
    loadMemberManagement();
  }, [loadMemberManagement]);

  const handleRemoveMember = async (member) => {
    if (
      !window.confirm(
        `Remove ${member.username} from the league? Their team slot will become available for the next join.`
      )
    ) {
      return;
    }
    setRemovingUserId(member.user_id);
    try {
      const result = await leagueMembershipApi.removeMember(leagueId, member.user_id, currentUser);
      if (showToast) {
        showToast(result.message || "Member removed", "success");
      }
      await loadMemberManagement();
      if (refreshLeagueSettings) {
        await refreshLeagueSettings();
      }
      await fetchLeagueSettingsFromApi();
    } catch (error) {
      if (showToast) {
        showToast(error.message || "Failed to remove member", "error");
      }
    } finally {
      setRemovingUserId(null);
    }
  };

  const formatTeamSlotLabel = (teamName) => {
    if (!teamName) return "—";
    const match = String(teamName).match(/^Team(\d+)$/i);
    return match ? `Team ${match[1]}` : teamName;
  };

  const handleTransferTeam = async (e) => {
    e.preventDefault();
    if (!transferEmail.trim() || !transferTeamName) {
      if (showToast) showToast("Enter an email and select a team slot", "error");
      return;
    }
    const slotLabel = formatTeamSlotLabel(transferTeamName);
    if (
      !window.confirm(
        `Assign ${transferEmail.trim()} to ${slotLabel}? They will inherit that team's roster. Any current manager on that slot will be removed.`
      )
    ) {
      return;
    }
    setTransferring(true);
    try {
      const result = await leagueMembershipApi.transferTeam(
        leagueId,
        transferTeamName,
        { email: transferEmail.trim() },
        currentUser
      );
      if (showToast) showToast(result.message || "Team transferred", "success");
      setTransferEmail("");
      setTransferTeamName("");
      await loadMemberManagement();
      if (refreshLeagueSettings) await refreshLeagueSettings();
    } catch (error) {
      if (showToast) showToast(error.message || "Transfer failed", "error");
    } finally {
      setTransferring(false);
    }
  };

  useEffect(() => {
    // Re-check lock state or any other logic when draftRefresh changes
    // (If you have a local isDraftStarted state, update it here)
  }, [draftRefresh]);

  const handleChange = (field, value) => {
    setSettings((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "rosterUnlockDay") {
        const day = normalizeUnlockDay(value);
        next.rosterUnlockDay = day;
        next.waiverRunDay = UNLOCK_DAY_TO_WAIVER_RUN[day] ?? 3;
      }
      if (field === "rosterUnlockHour") {
        next.waiverRunHour = value;
      }
      return next;
    });
  };

  const handleSave = async () => {
    // Send PATCH request to backend to update league name/settings
    try {
      // Validate required data
      if (!currentUser || !currentUser.id) {
        setSaveMsg("Error: User not authenticated. Please refresh the page and try again.");
        setTimeout(() => setSaveMsg(""), 5000);
        return;
      }
      
      const targetLeagueId = settings.id || leagueId;
      if (!targetLeagueId) {
        setSaveMsg("Error: League ID not found. Please refresh the page and try again.");
        setTimeout(() => setSaveMsg(""), 5000);
        return;
      }
      
      devLog("Saving league settings:", settings);

      const rosterTotal = getLeagueRosterCounts(settings).total;
      if (rosterTotal > 20) {
        setSaveMsg(`Total roster size is ${rosterTotal} players; maximum is 20. Reduce starter or bench slots.`);
        setTimeout(() => setSaveMsg(""), 5000);
        return;
      }
      
      // Map camelCase settings to snake_case payload expected by backend
      const payload = {};
      
      // Only include fields that are actually set (not empty strings for name)
      if (settings.name && settings.name.trim()) {
        try {
          payload.name = validateAllowedText(settings.name, {
            fieldName: "League name",
            maxLength: 64,
          });
        } catch (err) {
          setSaveMsg(err.message);
          setTimeout(() => setSaveMsg(""), 5000);
          return;
        }
      }
      
      payload.scoring_type = settings.scoringType;
      payload.draft_type = settings.draftType;
      if (settings.draftType === "SALARY_CAP" && settings.salaryCap != null) {
        payload.salary_cap = settings.salaryCap;
      }
      payload.faab_budget = settings.faabBudget;
      payload.use_faab = settings.useFAAB;
      payload.roster_unlock_day = normalizeUnlockDay(settings.rosterUnlockDay);
      payload.roster_unlock_hour = settings.rosterUnlockHour != null ? settings.rosterUnlockHour : 5;
      payload.trade_deadline_week = settings.tradeDeadlineWeek != null ? settings.tradeDeadlineWeek : 11;
      if (settings.is_public !== true) {
        payload.post_drop_waiver_days = settings.postDropWaiverDays != null ? settings.postDropWaiverDays : 1;
      }
      payload.team_count = settings.teamCount;
      payload.qb_count = settings.qbCount;
      payload.wr_count = settings.wrCount;
      payload.rb_count = settings.rbCount;
      payload.te_count = settings.teCount;
      payload.k_count = settings.kCount;
      payload.flex_count = settings.flexCount;
      if (settings.is_public !== true) {
        payload.flex_silver_count = settings.flexSilverCount != null ? Number(settings.flexSilverCount) : 0;
        payload.flex_gold_count = settings.flexGoldCount != null ? Number(settings.flexGoldCount) : 0;
      }
      payload.bench_count = settings.benchCount;
      if (settings.draftType === "SALARY_CAP" && settings.nominationTimerDuration != null) {
        payload.nomination_timer_duration = settings.nominationTimerDuration;
      }

      devLog("Sending payload:", payload);
      devLog("User ID:", currentUser.id);
      devLog("League ID:", targetLeagueId);

      const url = `${config.API_URL}/leagues/${targetLeagueId}?user_id=${currentUser.id}`;
      devLog("Request URL:", url);

      const res = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      devLog("Response status:", res.status);

      if (res.ok) {
        const result = await res.json();
        devLog("Save successful:", result);
        setSaveMsg("Settings saved successfully!");
        setTimeout(() => setSaveMsg(""), 3000);
        if (refreshLeagueSettings) {
          await refreshLeagueSettings();
        }
      } else {
        // Try to parse error response, but handle case where response might not be JSON
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        try {
          const errorData = await res.json();
          console.error('Save failed:', errorData);
          
          // Handle different error response formats
          if (errorData.detail) {
            if (Array.isArray(errorData.detail)) {
              // Handle validation error array
              errorMessage = errorData.detail.map(err => err.msg || err).join(', ');
            } else {
              // Handle single error message
              errorMessage = errorData.detail;
            }
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          // Use the default error message from status
        }
        
        setSaveMsg(`Failed to save settings: ${errorMessage}`);
        setTimeout(() => setSaveMsg(""), 5000);
        await fetchLeagueSettingsFromApi();
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      // Provide more helpful error messages
      let errorMessage = error.message;
      if (error.message === "Failed to fetch") {
        errorMessage = "Failed to connect to server. Please check if the backend is running and try again.";
      }
      setSaveMsg(`Error saving settings: ${errorMessage}`);
      setTimeout(() => setSaveMsg(""), 5000);
    }
  };

  // Show loading state while fetching league settings
  if (loading || !leagueSettings) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div>Loading league settings...</div>
      </div>
    );
  }

  let message = "";
  if (isPublicLeague) {
    message = "Public league settings cannot be modified.";
  } else if (isDraftStarted) {
    message = "League settings are locked after the draft has started.";
  } else if (userRole !== "commissioner") {
    message = "Only the commissioner can modify league settings.";
  }

  return (
    <div className="ffb-bg">
      <header className="ffb-header">
        <h1 className="ffb-header-title">🏈 League Settings 🏈</h1>
      </header>
      <div style={{
        padding: 32,
        paddingBottom: canEdit ? 100 : 32,
        maxWidth: 800,
        margin: "0 auto"
      }}>
        <button
          onClick={onBack}
          className="ffb-btn"
          style={{ marginBottom: 24, padding: "8px 20px", fontSize: 16, borderRadius: 4 }}
        >
          Back to Home
        </button>
        
        {/* User and Role Information */}
        {currentUser && (
          <div style={{ 
            background: "#f0f9ff", 
            padding: 16, 
            borderRadius: 8, 
            marginBottom: 24,
            border: "1px solid #0ea5e9"
          }}>
            <h3 style={{ margin: "0 0 8px 0", color: "#0c4a6e" }}>User Information</h3>
            <p style={{ margin: "4px 0", color: "#0c4a6e" }}>
              <strong>Username:</strong> {currentUser.username}
            </p>
            <p style={{ margin: "4px 0", color: "#0c4a6e" }}>
              <strong>Role:</strong> {userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1) : "Not a member"}
            </p>
            <p style={{ margin: "4px 0", color: "#0c4a6e" }}>
              <strong>Can Edit Settings:</strong> {canEdit ? "Yes" : "No"}
            </p>
          </div>
        )}

        {/* Draft Schedule Information */}
        <div style={{ 
          background: "#fff", 
          padding: 16, 
          borderRadius: 8, 
          marginBottom: 24,
          border: "1px solid #e2e8f0",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
        }}>
          <h3 style={{ margin: "0 0 12px 0", color: "#2563eb" }}>📅 Draft Schedule</h3>
                      {settings.draft_scheduled ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ 
                fontSize: 18, 
                fontWeight: 600, 
                color: "#059669", 
                marginBottom: 8 
              }}>
                🎯 Draft Scheduled!
              </div>
              <div style={{ 
                fontSize: 16, 
                color: "#374151", 
                marginBottom: 16 
              }}>
                {(() => {
                  try {
                    return formatDraftTimeEastern(settings.draft_date, settings.draft_time) ||
                      `${settings.draft_date} at ${settings.draft_time}`;
                  } catch (error) {
                    return `${settings.draft_date} at ${settings.draft_time}`;
                  }
                })()}
              </div>
              <div style={{ fontSize: 14, color: "#6b7280" }}>
                Eastern time (ET)
              </div>
            </div>
          ) : (
            <div style={{ textAlign: "center" }}>
              <div style={{ 
                fontSize: 16, 
                color: "#64748b", 
                marginBottom: 8 
              }}>
                Draft date/time not yet set
              </div>
              <div style={{ fontSize: 14, color: "#6b7280" }}>
                Go to Live Draft page to schedule your draft
              </div>
            </div>
          )}
        </div>
        
        {message && (
          <div style={{
            background: "#fef2f2",
            color: "#dc2626",
            padding: 16,
            borderRadius: 8,
            marginBottom: 24,
            border: "1px solid #fecaca"
          }}>
            {message}
          </div>
        )}
        {/* Read-only view for public leagues */}
        {isPublicLeague ? (
          <>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>League Name:</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.name}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Scoring Type:</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{SCORING_OPTIONS.find(opt => opt.value === settings.scoringType)?.label || settings.scoringType}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Draft Type:</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{DRAFT_TYPE_OPTIONS.find(opt => opt.value === settings.draftType)?.label || settings.draftType}</div>
            </div>
            {(settings.draftType || "SNAKE") === "SALARY_CAP" && (
              <>
                <div style={{ marginBottom: 18 }}>
                  <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Salary Cap Budget:</label>
                  <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>${(settings.salaryCap != null ? settings.salaryCap : 200).toLocaleString()}</div>
                </div>
                <div style={{ marginBottom: 18 }}>
                  <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Nomination Countdown Timer:</label>
                  <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.nominationTimerDuration != null ? `${settings.nominationTimerDuration} seconds` : "45 seconds (default)"}</div>
                </div>
              </>
            )}
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of Teams:</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.teamCount || 10}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of QBs (Starters):</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.qbCount || 1}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of WRs (Starters):</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.wrCount || 2}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of RBs (Starters):</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.rbCount || 2}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of TEs (Starters):</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.teCount || 1}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of FLEX (WR/RB/TE Starters):</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.flexCount || 1}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of FLEX of Silver (WR/RB/TE Starters):</label>
              <div style={{ padding: 8, background: "#e2e8f0", borderRadius: 4, borderLeft: "4px solid #94a3b8" }}>{settings.flexSilverCount ?? 0}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of FLEX of Gold (WR/RB/TE Starters):</label>
              <div style={{ padding: 8, background: "#fef3c7", borderRadius: 4, borderLeft: "4px solid #d97706" }}>{settings.flexGoldCount ?? 0}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of Ks (Starters):</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.kCount ?? 1}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Bench Slots:</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.benchCount ?? 5}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>FAAB Budget:</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.faabBudget || 100}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Waiver System:</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.useFAAB ? "FAAB" : "Standard Waivers"}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Roster Unlock Day:</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.rosterUnlockDay ? settings.rosterUnlockDay.charAt(0).toUpperCase() + settings.rosterUnlockDay.slice(1) : 'Wednesday'}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Roster Unlock Hour (Eastern):</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{formatUnlockHour(settings.rosterUnlockHour)}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Trade Deadline Week:</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>Week {(settings.tradeDeadlineWeek != null ? settings.tradeDeadlineWeek : 11)}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Waiver Run Day:</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][settings.waiverRunDay != null ? settings.waiverRunDay : 3]}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Waiver Run Hour (Eastern):</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{formatUnlockHour(settings.waiverRunHour != null ? settings.waiverRunHour : 5)}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Post-Drop Waiver Period:</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.postDropWaiverDays || 2}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>FAAB Budget (per team):</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.faabBudget ?? 100}</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Playoff Teams:</label>
              <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.playoffTeams || 4}</div>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>League Name:</label>
              <input
                type="text"
                value={settings.name}
                onChange={e => handleChange("name", e.target.value)}
                style={{ padding: 8, fontSize: 16, borderRadius: 4, border: "1px solid #cbd5e1", width: "100%" }}
                disabled={isPublicLeague || userRole !== "commissioner"}
              />
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Scoring Type:</label>
              <select
                value={settings.scoringType}
                onChange={e => handleChange("scoringType", e.target.value)}
                className="ffb-select"
                disabled={!canEdit}
              >
                {SCORING_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Draft Type:</label>
              <select
                value={settings.draftType || "SNAKE"}
                onChange={e => handleChange("draftType", e.target.value)}
                className="ffb-select"
                disabled={!canEdit}
              >
                {DRAFT_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {(settings.draftType || "SNAKE") === "SALARY_CAP" && (
              <>
                <div style={{ marginBottom: 18 }}>
                  <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Salary Cap Budget:</label>
                  <select
                    value={normalizeSalaryCap(settings.salaryCap)}
                    onChange={e => handleChange("salaryCap", Number(e.target.value))}
                    className="ffb-select"
                    disabled={!canEdit}
                  >
                    {SALARY_CAP_BUDGET_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <div style={{ fontSize: "0.9em", color: "#64748b", marginTop: 4 }}>
                    Budget per team for bidding on players during the draft.
                  </div>
                </div>
                <div style={{ marginBottom: 18 }}>
                  <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Nomination Countdown Timer:</label>
                  <select
                    value={settings.nominationTimerDuration != null ? settings.nominationTimerDuration : 45}
                    onChange={e => handleChange("nominationTimerDuration", Number(e.target.value))}
                    className="ffb-select"
                    disabled={!canEdit}
                  >
                    {NOMINATION_TIMER_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <div style={{ fontSize: "0.9em", color: "#64748b", marginTop: 4 }}>
                    Time each team has to nominate a player before the highest-value remaining player is auto-nominated.
                  </div>
                </div>
              </>
            )}

            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of Teams:</label>
              {isPublicLeague ? (
                <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.teamCount}</div>
              ) : (
                <select
                  value={settings.teamCount || 10}
                  onChange={e => handleChange("teamCount", Number(e.target.value))}
                  className="ffb-select"
                  disabled={!canEdit}
                >
                  {Array.from({ length: 19 }, (_, i) => i + 2).map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              )}
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of QBs (Starters):</label>
              <select
                value={settings.qbCount || 1}
                onChange={e => handleChange("qbCount", Number(e.target.value))}
                className="ffb-select"
                disabled={!canEdit}
              >
                {Array.from({ length: 4 }, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of WRs (Starters):</label>
              <select
                value={settings.wrCount || 2}
                onChange={e => handleChange("wrCount", Number(e.target.value))}
                className="ffb-select"
                disabled={!canEdit}
              >
                {Array.from({ length: 4 }, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of RBs (Starters):</label>
              <select
                value={settings.rbCount || 2}
                onChange={e => handleChange("rbCount", Number(e.target.value))}
                className="ffb-select"
                disabled={!canEdit}
              >
                {Array.from({ length: 4 }, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of TEs (Starters):</label>
              <select
                value={settings.teCount || 1}
                onChange={e => handleChange("teCount", Number(e.target.value))}
                className="ffb-select"
                disabled={!canEdit}
              >
                {Array.from({ length: 4 }, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of FLEX (WR/RB/TE Starters):</label>
              <select
                value={settings.flexCount || 1}
                onChange={e => handleChange("flexCount", Number(e.target.value))}
                className="ffb-select"
                disabled={!canEdit}
              >
                {Array.from({ length: 4 }, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 18, padding: 12, borderRadius: 8, background: "linear-gradient(90deg, #f8fafc 0%, #e2e8f0 100%)", border: "1px solid #cbd5e1", borderLeft: "4px solid #94a3b8" }}>
              <label style={{ fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Number of FLEX of Silver (WR/RB/TE Starters):</label>
              <select
                value={settings.flexSilverCount ?? 0}
                onChange={e => handleChange("flexSilverCount", Number(e.target.value))}
                className="ffb-select"
                disabled={!canEdit}
              >
                <option value={0}>0</option>
                <option value={1}>1</option>
              </select>
              <div style={{ fontSize: "0.85em", color: "#64748b", marginTop: 6 }}>Starter slot: same positions as FLEX; fantasy points for the week count at 1.5× base scoring.</div>
            </div>
            <div style={{ marginBottom: 18, padding: 12, borderRadius: 8, background: "linear-gradient(90deg, #fffbeb 0%, #fef3c7 100%)", border: "1px solid #fcd34d", borderLeft: "4px solid #d97706" }}>
              <label style={{ fontWeight: 600, color: "#92400e", display: "block", marginBottom: 6 }}>Number of FLEX of Gold (WR/RB/TE Starters):</label>
              <select
                value={settings.flexGoldCount ?? 0}
                onChange={e => handleChange("flexGoldCount", Number(e.target.value))}
                className="ffb-select"
                disabled={!canEdit}
              >
                <option value={0}>0</option>
                <option value={1}>1</option>
              </select>
              <div style={{ fontSize: "0.85em", color: "#92400e", marginTop: 6 }}>Starter slot: same positions as FLEX; fantasy points for the week count at 2× base scoring.</div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of Ks (Starters):</label>
              {canEdit ? (
                <select
                  value={settings.kCount ?? 1}
                  onChange={e => handleChange("kCount", Number(e.target.value))}
                  className="ffb-select"
                  disabled={!canEdit}
                >
                  {[0, 1, 2].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              ) : (
                <div style={{ padding: 8, background: "#f1f5f9", borderRadius: 4 }}>{settings.kCount ?? 1}</div>
              )}
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Number of Bench Slots:</label>
              <select
                value={settings.benchCount ?? 5}
                onChange={e => handleChange("benchCount", Number(e.target.value))}
                className="ffb-select"
                disabled={!canEdit}
              >
                {Array.from({ length: 8 }, (_, i) => i).map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Waiver System:</label>
              <select
                value={settings.useFAAB ? "FAAB" : "WAIVERS"}
                onChange={e => handleChange("useFAAB", e.target.value === "FAAB")}
                className="ffb-select"
                disabled={!canEdit}
              >
                <option value="WAIVERS">Traditional Waivers</option>
                <option value="FAAB">FAAB (Bidding)</option>
              </select>
              <div style={{ fontSize: "0.9em", color: "#64748b", marginTop: 4 }}>
                Choose how free agents are acquired in season: traditional waiver claims (waiver priority, first come, first served when open) or FAAB bidding (processed at the weekly waiver run). After the draft until Week 1 kickoff, all leagues use first come, first served (FCFS) regardless of this setting.
              </div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>
                {settings.useFAAB ? "Roster Unlock (Eastern)" : "Weekly Waiver Processing (Eastern)"}:
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 8 }}>
                <select
                  value={normalizeUnlockDay(settings.rosterUnlockDay)}
                  onChange={e => handleChange("rosterUnlockDay", e.target.value)}
                  className="ffb-select"
                  disabled={!canEdit}
                  style={{ minWidth: 180 }}
                >
                  <option value="tuesday">Tuesday</option>
                  <option value="wednesday">Wednesday (default)</option>
                </select>
                <select
                  value={settings.rosterUnlockHour != null ? settings.rosterUnlockHour : 5}
                  onChange={e => handleChange("rosterUnlockHour", Number(e.target.value))}
                  className="ffb-select"
                  disabled={!canEdit}
                  style={{ minWidth: 160 }}
                >
                  <option value={1}>1:00 AM ET</option>
                  <option value={3}>3:00 AM ET</option>
                  <option value={5}>5:00 AM ET (default)</option>
                  <option value={8}>8:00 AM ET</option>
                  <option value={12}>12:00 PM ET</option>
                </select>
              </div>
              <div style={{ fontSize: "0.9em", color: "#64748b", marginTop: 4 }}>
                {isPublicLeague
                  ? `Public leagues use platform defaults: ${getPlatformDefaultWaiverSummary({ useFaab: settings.useFAAB })}`
                  : getLeagueSettingsWaiverCopy({
                      unlockDay: settings.rosterUnlockDay,
                      unlockHour: settings.rosterUnlockHour,
                      useFaab: settings.useFAAB,
                    })}
              </div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Trade Deadline Week:</label>
              <select
                value={settings.tradeDeadlineWeek != null ? settings.tradeDeadlineWeek : 11}
                onChange={e => handleChange("tradeDeadlineWeek", Number(e.target.value))}
                className="ffb-select"
                disabled={!canEdit}
              >
                {Array.from({ length: 18 }, (_, i) => i + 1).map(w => (
                  <option key={w} value={w}>Week {w}{w === 11 ? ' (default)' : ''}</option>
                ))}
              </select>
              <div style={{ fontSize: "0.9em", color: "#64748b", marginTop: 4 }}>
                Trades allowed through this week; no trades after (rest of season).
              </div>
            </div>
            {!isPublicLeague && (
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Post-Drop Waiver Period:</label>
              <select
                value={settings.postDropWaiverDays != null ? settings.postDropWaiverDays : 1}
                onChange={e => handleChange("postDropWaiverDays", Number(e.target.value))}
                className="ffb-select"
                disabled={!canEdit}
              >
                <option value={1}>1 day (default)</option>
                <option value={2}>2 days</option>
                <option value={3}>3 days</option>
              </select>
              <div style={{ fontSize: "0.9em", color: "#64748b", marginTop: 4 }}>
                {getPostDropWaiverSettingsCopy()}
              </div>
            </div>
            )}
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>FAAB Budget (per team):</label>
              <select
                value={settings.faabBudget ?? 100}
                onChange={e => handleChange("faabBudget", Number(e.target.value))}
                className="ffb-select"
                disabled={!canEdit}
              >
                {Array.from({ length: 10 }, (_, i) => (i + 1) * 100).map(amount => (
                  <option key={amount} value={amount}>${amount}</option>
                ))}
              </select>
              <div style={{ fontSize: "0.9em", color: "#64748b", marginTop: 4 }}>
                Free Agent Acquisition Budget - each team starts with this amount for bidding on free agents
              </div>
            </div>
            {/* Add playoff team count setting (commissioner, private league only) */}
            {canEdit && (
              <div style={{ marginBottom: 18 }}>
                <label style={{ fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>Playoff Teams:</label>
                <select
                  value={settings.playoffTeams}
                  onChange={e => handleChange("playoffTeams", Number(e.target.value))}
                  className="ffb-select"
                  disabled={!canEdit}
                >
                  {PLAYOFF_TEAM_OPTIONS.filter(opt => opt <= settings.teamCount).map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>
                  Only 2, 4, 6, or 8 teams allowed. Default is based on league size.
                </div>
              </div>
            )}

            {/* Commissioner Tools Section */}
            {(!isPublicLeague && userRole === "commissioner") && (
              <div style={{ marginTop: 32, padding: 20, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <h3 style={{ margin: '0 0 16px 0', color: '#374151' }}>Commissioner Tools</h3>

                <div style={{ marginBottom: 24 }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#475569', fontSize: 15 }}>League members</h4>
                  {membersLoading ? (
                    <p style={{ color: '#64748b', margin: 0 }}>Loading members…</p>
                  ) : (
                    <>
                      <TeamSlotGrid slots={teamSlots} title="Slot overview" />
                      {leagueMembers.length === 0 ? (
                        <p style={{ color: '#64748b', margin: '12px 0 0' }}>No members yet.</p>
                      ) : (
                        <div style={{ marginTop: 16, overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                            <thead>
                              <tr style={{ background: '#e2e8f0', textAlign: 'left' }}>
                                <th style={{ padding: '8px 12px' }}>User</th>
                                <th style={{ padding: '8px 12px' }}>Role</th>
                                <th style={{ padding: '8px 12px' }}>Slot</th>
                                <th style={{ padding: '8px 12px' }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {leagueMembers.map((member) => {
                                const isSelf = member.user_id === currentUser?.id;
                                return (
                                  <tr key={member.user_id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                    <td style={{ padding: '10px 12px' }}>{member.username}</td>
                                    <td style={{ padding: '10px 12px', textTransform: 'capitalize' }}>{member.role}</td>
                                    <td style={{ padding: '10px 12px' }}>{formatTeamSlotLabel(member.team_name)}</td>
                                    <td style={{ padding: '10px 12px' }}>
                                      {isSelf ? (
                                        <span style={{ color: '#94a3b8', fontSize: 13 }}>You</span>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveMember(member)}
                                          disabled={removingUserId === member.user_id}
                                          style={{
                                            background: '#dc2626',
                                            color: 'white',
                                            border: 'none',
                                            padding: '6px 12px',
                                            borderRadius: 4,
                                            fontSize: 13,
                                            fontWeight: 600,
                                            cursor: removingUserId === member.user_id ? 'wait' : 'pointer',
                                            opacity: removingUserId === member.user_id ? 0.7 : 1,
                                          }}
                                        >
                                          {removingUserId === member.user_id ? 'Removing…' : 'Remove'}
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div
                  style={{
                    marginBottom: 24,
                    padding: 16,
                    background: '#fff',
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <h4 style={{ margin: '0 0 8px 0', color: '#475569', fontSize: 15 }}>
                    Transfer team (replacement manager)
                  </h4>
                  <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: 13, lineHeight: 1.5 }}>
                    {isDraftStarted
                      ? "After the draft has started, assign a registered user to a team slot. They inherit that roster. Use this instead of email invites."
                      : "Optionally pre-assign a user to a specific slot before the draft. For new managers before the draft, email invites are usually easier."}
                  </p>
                  <form
                    onSubmit={handleTransferTeam}
                    style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}
                  >
                    <label style={{ flex: '1 1 200px' }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                        User email
                      </span>
                      <input
                        type="email"
                        value={transferEmail}
                        onChange={(e) => setTransferEmail(e.target.value)}
                        placeholder="manager@example.com"
                        className="ffb-input"
                        style={{ width: '100%' }}
                        disabled={transferring}
                      />
                    </label>
                    <label style={{ flex: '0 1 160px' }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                        Team slot
                      </span>
                      <select
                        value={transferTeamName}
                        onChange={(e) => setTransferTeamName(e.target.value)}
                        className="ffb-select"
                        style={{ width: '100%' }}
                        disabled={transferring}
                        required
                      >
                        <option value="">Select slot…</option>
                        {teamSlots.map((slot) => (
                          <option key={slot.team_name} value={slot.team_name}>
                            {formatTeamSlotLabel(slot.team_name)}
                            {slot.filled ? " (replaces current)" : " (open)"}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="submit"
                      disabled={transferring || !transferEmail.trim() || !transferTeamName}
                      className="ffb-btn"
                      style={{
                        background: '#2563eb',
                        opacity: transferring ? 0.7 : 1,
                      }}
                    >
                      {transferring ? "Assigning…" : "Transfer team"}
                    </button>
                  </form>
                </div>

                <div
                  style={{
                    marginBottom: 24,
                    padding: 16,
                    background: '#f0fdf4',
                    borderRadius: 8,
                    border: '1px solid #bbf7d0',
                  }}
                >
                  <h4 style={{ margin: '0 0 8px 0', color: '#166534', fontSize: 15 }}>
                    Invite league members
                  </h4>
                  <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: 13, lineHeight: 1.5 }}>
                    Send email invites before the draft. Friends create an account (or log in) and accept the link to claim an open team slot. Invited managers join free — only the commissioner pays the one-time $40 inaugural-season platform fee.
                  </p>
                  {isDraftStarted && (
                    <p style={{ margin: '0 0 12px', color: '#92400e', fontSize: 13, lineHeight: 1.5 }}>
                      The draft has started — use <strong>Transfer team</strong> above to assign a replacement manager instead of email invites.
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setShowInviteManager(true)}
                    disabled={isDraftStarted}
                    style={{
                      background: isDraftStarted ? '#9ca3af' : '#059669',
                      color: 'white',
                      padding: '10px 20px',
                      fontSize: 14,
                      border: 'none',
                      borderRadius: 6,
                      fontWeight: 600,
                      cursor: isDraftStarted ? 'not-allowed' : 'pointer'
                    }}
                  >
                    📧 Manage Invites
                  </button>
                  </div>
                </div>

                {isAdminAuthenticated && (
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
                    <button
                      onClick={() => {
                        if (typeof onTestLock === 'function') {
                          onTestLock();
                        } else {
                          if (onBack) onBack();
                          if (typeof window.setPage === 'function') window.setPage('locktest');
                        }
                      }}
                      style={{
                        background: '#f59e0b',
                        color: 'white',
                        padding: '10px 20px',
                        fontSize: 14,
                        border: 'none',
                        borderRadius: 6,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      🔒 Test Lock System
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Sticky save bar: always visible for commissioner when settings are editable */}
      {canEdit && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            background: "#fff",
            borderTop: "1px solid #e2e8f0",
            boxShadow: "0 -4px 6px -1px rgba(0,0,0,0.08)",
            padding: "12px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={handleSave}
            style={{
              padding: "10px 32px",
              fontSize: 16,
              background: "#2563eb",
              color: "white",
              border: "none",
              borderRadius: 4,
              fontWeight: 700,
              cursor: "pointer",
            }}
            className="ffb-btn"
          >
            Save Settings
          </button>
          {saveMsg && (
            <span style={{
              color: saveMsg.startsWith("Settings saved") ? "#22c55e" : "#dc2626",
              fontWeight: 600,
              fontSize: 14,
            }}>
              {saveMsg}
            </span>
          )}
        </div>
      )}

      {showInviteManager && (
        <LeagueInviteManager
          leagueId={settings.id || leagueId}
          currentUser={currentUser}
          onClose={() => setShowInviteManager(false)}
          showToast={showToast || ((message, type) => alert(`${type === 'error' ? 'Error: ' : ''}${message}`))}
          draftHasBegun={isDraftStarted}
        />
      )}
    </div>
  );
} 